(() => {
  // External web destinations open separately; storefront and email links stay local.
  document.querySelectorAll('a[href]').forEach(link => {
    const destination = new URL(link.getAttribute('href'), window.location.href);
    if (['http:', 'https:'].includes(destination.protocol) && destination.hostname !== window.location.hostname) {
      link.target = '_blank';
      link.relList.add('noopener', 'noreferrer');
    }
  });
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!reduced && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) { entry.target.classList.add('cm-visible'); observer.unobserve(entry.target); }
    }), { threshold: 0.08 });
    document.querySelectorAll('[data-cm-reveal]').forEach(el => observer.observe(el));
    document.documentElement.classList.add('cm-reveal-ready');
    document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-cm-reveal]').forEach(el => observer.observe(el)));
  }
  function initializeCarousels(root = document) {
    root.querySelectorAll('[data-cm-carousel]').forEach(carousel => {
      if (carousel.dataset.initialized) return;
      carousel.dataset.initialized = 'true';
      const slides = [...carousel.querySelectorAll('[data-cm-slide]')];
      if (!slides.length) return;
      // Project screenshots onto Frame 133's screen plane; the shell itself is already angled.
      carousel.querySelectorAll('[data-cm-oblique-screen]').forEach(screen => {
        const plane = screen.querySelector('.cm-carousel-screen-plane');
        const fit = () => {
          const width = parseFloat(getComputedStyle(screen).width);
          if (!width) return;
          plane.style.transform = `matrix3d(.5114035236,-.0489218259,0,${.0323989796 / width},.386807652,.9144239966,0,${-.0344725789 / width},0,0,1,0,${.0025478109 * width},${.0510729897 * width},0,1)`;
        };
        fit();
        if ('ResizeObserver' in window) new ResizeObserver(fit).observe(screen);
        else window.addEventListener('resize', fit, { passive: true });
      });
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const direction = carousel.classList.contains('cm-carousel-summary') ? -1 : 1;
      let index = 0, timer, visible = false, stopped = motion.matches, hovering = false, focused = false;
      const render = (announce = false) => {
        slides.forEach((slide, i) => {
          let offset = (i - index + slides.length) % slides.length;
          if (offset > slides.length / 2) offset -= slides.length;
          slide.dataset.position = String(Math.abs(offset) > 1 ? 3 : offset);
          slide.setAttribute('aria-hidden', String(offset !== 0));
        });
        const label = slides[index].getAttribute('aria-label');
        carousel.querySelector('[data-cm-caption]').textContent = label;
        if (announce) carousel.querySelector('[data-cm-status]').textContent = `${label}, screen ${index + 1} of ${slides.length}`;
      };
      const schedule = () => {
        clearTimeout(timer);
        if (visible && !stopped && !motion.matches && !hovering && !focused && !document.hidden) timer = setTimeout(() => { index = (index + direction + slides.length) % slides.length; render(); schedule(); }, 5500);
      };
      const move = direction => { index = (index + direction + slides.length) % slides.length; render(true); schedule(); };
      carousel.querySelector('[data-cm-prev]').addEventListener('click', () => move(-1));
      carousel.querySelector('[data-cm-next]').addEventListener('click', () => move(1));
      carousel.addEventListener('mouseenter', () => { hovering = true; schedule(); });
      carousel.addEventListener('mouseleave', () => { hovering = false; schedule(); });
      carousel.addEventListener('focusin', () => { focused = true; schedule(); });
      carousel.addEventListener('focusout', event => { focused = carousel.contains(event.relatedTarget); schedule(); });
      carousel.addEventListener('keydown', event => { if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1); } });
      let touchX;
      carousel.addEventListener('touchstart', event => { touchX = event.changedTouches[0].clientX; }, { passive: true });
      carousel.addEventListener('touchend', event => { const dx = event.changedTouches[0].clientX - touchX; if (Math.abs(dx) > 40) move(dx < 0 ? 1 : -1); }, { passive: true });
      motion.addEventListener('change', () => { stopped = motion.matches; schedule(); });
      document.addEventListener('visibilitychange', schedule);
      if ('IntersectionObserver' in window) new IntersectionObserver(entries => { visible = entries[0].isIntersecting; schedule(); }, { threshold: .25 }).observe(carousel);
      render(); schedule();
    });
  }
  initializeCarousels();
  document.addEventListener('shopify:section:load', event => initializeCarousels(event.target));
  function initializeHero(root = document) {
    root.querySelectorAll('[data-cm-hero-video]').forEach(video => {
      if (video.dataset.initialized) return;
      video.dataset.initialized = 'true';
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const start = () => {
        if (motion.matches || navigator.connection?.saveData) return;
        if (!video.src) { video.src = video.dataset.src; video.muted = true; }
        video.play().catch(() => {});
      };
      video.addEventListener('playing', () => video.classList.add('cm-playing'));
      motion.addEventListener('change', () => {
        if (motion.matches) { video.pause(); video.removeAttribute('src'); video.load(); video.classList.remove('cm-playing'); }
        else start();
      });
      if (document.readyState === 'complete') start();
      else window.addEventListener('load', start, { once: true });
    });
  }
  initializeHero();
  document.addEventListener('shopify:section:load', event => initializeHero(event.target));
  document.querySelectorAll('.cm-mobile-menu a').forEach(link => link.addEventListener('click', () => link.closest('details').removeAttribute('open')));
  function initializeInteractions(root = document) {
    root.querySelectorAll('.cm-details').forEach(detail => {
      if (detail.dataset.cmAnimated) return;
      detail.dataset.cmAnimated = 'true';
      const summary = detail.querySelector('summary');
      let animation, targetOpen = detail.open;
      const transition = opening => {
        const start = detail.getBoundingClientRect().height;
        animation?.cancel();
        targetOpen = opening;
        detail.open = true;
        detail.style.height = '';
        const end = opening ? detail.getBoundingClientRect().height : summary.getBoundingClientRect().height;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { detail.open = opening; return; }
        detail.style.overflow = 'hidden';
        detail.style.height = `${start}px`;
        animation = detail.animate({ height: [`${start}px`, `${end}px`] }, { duration: 280, easing: 'cubic-bezier(.22,.61,.36,1)' });
        animation.onfinish = () => { detail.open = targetOpen; detail.style.height = ''; detail.style.overflow = ''; animation = null; };
      };
      detail.cmTransition = transition;
      summary.addEventListener('click', event => {
        event.preventDefault();
        const opening = !targetOpen;
        if (opening) detail.closest('section').querySelectorAll('.cm-details').forEach(other => { if (other !== detail && other.open) other.cmTransition?.(false); });
        transition(opening);
      });
    });
  }
  initializeInteractions();
  document.addEventListener('shopify:section:load', event => initializeInteractions(event.target));
  const header = document.querySelector('.cm-header');
  const desktopNav = header?.querySelector('.cm-desktop-nav');
  const cartLink = header?.querySelector('[data-cm-cart-link]');
  const updateCartCount = count => {
    if (!cartLink || !Number.isFinite(Number(count))) return;
    const badge = cartLink.querySelector('[data-cm-cart-count]');
    badge.textContent = String(count);
    badge.hidden = Number(count) === 0;
    cartLink.setAttribute('aria-label', `Cart, ${count} ${Number(count) === 1 ? 'item' : 'items'}`);
  };
  window.addEventListener('pageshow', event => {
    if (event.persisted && cartLink) fetch(`${cartLink.dataset.cmCartUrl}.js`)
      .then(response => response.ok ? response.json() : Promise.reject())
      .then(cart => updateCartCount(cart.item_count)).catch(() => {});
  });
  if (cartLink && document.querySelector('cart-items-component')) {
    import('@shopify/events').then(({ StandardEvents }) => {
      document.addEventListener(StandardEvents.cartLinesUpdate, event => {
        event.promise?.then(({ cart, detail }) => updateCartCount(cart?.totalQuantity ?? detail?.itemCount ?? 0)).catch(() => {});
      });
    }).catch(() => {});
  }

  const fitNavPill = () => {
    if (!desktopNav || !desktopNav.offsetWidth) return;
    header.style.setProperty('--cm-nav-left', `${desktopNav.offsetLeft}px`);
    header.style.setProperty('--cm-nav-top', `${desktopNav.offsetTop}px`);
    header.style.setProperty('--cm-nav-width', `${desktopNav.offsetWidth}px`);
    header.style.setProperty('--cm-nav-height', `${desktopNav.offsetHeight}px`);
  };
  fitNavPill();
  if (desktopNav && 'ResizeObserver' in window) {
    const navObserver = new ResizeObserver(fitNavPill);
    navObserver.observe(header);
    navObserver.observe(desktopNav);
  }
  window.addEventListener('resize', fitNavPill, { passive: true });
  document.fonts?.ready.then(fitNavPill);
  let headerReturnTimer;
  const updateHeader = () => {
    if (!header) return;
    const scrolled = header.hasAttribute('data-cm-header-compact') || window.scrollY > 40;
    if (scrolled === header.classList.contains('cm-header-scrolled')) return;
    clearTimeout(headerReturnTimer);
    header.classList.remove('cm-header-returning');
    if (!scrolled && !reduced) {
      header.style.setProperty('--cm-full-pill-height', `${header.offsetHeight}px`);
      header.classList.add('cm-header-returning');
      headerReturnTimer = setTimeout(() => header.classList.remove('cm-header-returning'), 500);
    }
    header.classList.toggle('cm-header-scrolled', scrolled);
  };
  window.addEventListener('scroll', updateHeader, { passive: true });
  updateHeader();
  document.querySelectorAll('.cm-header a[href*="#"], .cm-footer a[href*="#"]').forEach(link => link.addEventListener('click', event => {
    const url = new URL(link.href);
    if (url.origin !== location.origin || url.pathname !== location.pathname) return;
    const target = document.getElementById(url.hash.slice(1));
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    history.replaceState(null, '', url.hash);
  }));
  // Load color images only when a decorative band enters the viewport.
  function initializePrivacyCycles(scope = document) {
    scope.querySelectorAll('[data-cm-privacy-cycle]').forEach(root => {
      if (root.dataset.initialized) return;
      root.dataset.initialized = 'true';
      const base = root.querySelector('img'), next = root.querySelector('.cm-privacy-next');
      const sources = JSON.parse(next.dataset.cmPrivacyImages);
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      let index = 0, visible = false, timer, loading;
      const stop = () => { clearInterval(timer); timer = undefined; };
      next.addEventListener('animationend', () => {
        base.src = sources[index];
        root.classList.remove('is-revealing');
      });
      const start = async () => {
        if (!visible || document.hidden || motion.matches || timer) return;
        loading ||= Promise.all(sources.map(src => {
          const image = new Image(); image.src = src; return image.decode();
        })).then(() => true).catch(() => false);
        if (!await loading || !visible || document.hidden || motion.matches || timer || !root.isConnected) return;
        timer = setInterval(() => {
          if (!root.isConnected) { stop(); observer.disconnect(); return; }
          index = (index + 1) % sources.length;
          next.src = sources[index]; root.classList.add('is-revealing');
        }, 3000);
      };
      const observer = new IntersectionObserver(entries => {
        visible = entries[0].isIntersecting; visible ? start() : stop();
      }, {threshold: .15});
      observer.observe(root);
      document.addEventListener('visibilitychange', () => document.hidden ? stop() : start());
      motion.addEventListener('change', () => {
        if (motion.matches) { stop(); root.classList.remove('is-revealing'); index = 0; base.src = sources[0]; }
        else start();
      });
    });
  }
  initializePrivacyCycles();
  document.addEventListener('shopify:section:load', event => initializePrivacyCycles(event.target));
  function initializeBandCycles(scope = document) {
    scope.querySelectorAll('[data-cm-band-cycle]').forEach(image => {
      if (image.dataset.cycleInitialized) return;
      image.dataset.cycleInitialized = 'true';
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const sources = [image.getAttribute('src'), ...JSON.parse(image.dataset.cmBandColors)];
      let index = 0, visible = false, selected = false, timer, loaded, transition;
      const stop = () => { clearInterval(timer); timer = undefined; };
      const restore = () => {
        stop(); clearTimeout(transition); index = 0;
        image.src = sources[0]; image.classList.remove('cm-band-fading');
      };
      const start = async () => {
        if (selected || !visible || document.hidden || motion.matches || timer) return;
        loaded ||= Promise.all(sources.slice(1).map(src => {
          const next = new Image(); next.src = src;
          return next.decode().then(() => src).catch(() => null);
        }));
        const ready = await loaded;
        if (selected || !visible || document.hidden || motion.matches || timer || !image.isConnected) return;
        const frames = [sources[0], ...ready.filter(Boolean)];
        if (frames.length < 2) return;
        timer = setInterval(() => {
          if (!image.isConnected) { stop(); observer.disconnect(); return; }
          image.classList.add('cm-band-fading');
          transition = setTimeout(() => {
            index = (index + 1) % frames.length;
            image.src = frames[index];
            image.classList.remove('cm-band-fading');
          }, 220);
        }, 3500);
      };
      const observer = new IntersectionObserver(entries => {
        visible = entries[0].isIntersecting;
        if (visible) start(); else stop();
      }, { threshold: 0.15 });
      observer.observe(image);
      image.addEventListener('cm:band-select', () => { selected = true; restore(); observer.disconnect(); });
      document.addEventListener('visibilitychange', () => document.hidden ? stop() : start());
      motion.addEventListener('change', () => motion.matches ? restore() : start());
    });
  }
  initializeBandCycles();
  document.addEventListener('shopify:section:load', event => initializeBandCycles(event.target));
  function initializePricing(root) {
    if (!root || root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const config = JSON.parse(root.querySelector('[data-cm-config]').textContent);
    const state = { plan: config.earlyBirdActive ? 'earlybird' : 'standard', hardware: true, color: 'Ink', size: '' };
    const money = cents => new Intl.NumberFormat(config.locale, { style: 'currency', currency: config.currency }).format(cents / 100);
    const acknowledgment = root.querySelector('[data-cm-ack]');
    const checkout = root.querySelector('[data-cm-checkout]');
    const error = root.querySelector('[data-cm-error]');
    function selectedVariant(product = config.hardware) {
      const match = product.variants.find(v => v.available && v.options.some(option => option.toLowerCase() === state.color.toLowerCase()));
      return match || (product.variants.length === 1 && product.variants[0].available ? product.variants[0] : undefined);
    }
    const annualVariant = plan => config.annual[plan]?.variants.find(variant => variant.available);
    const annualPrice = plan => config.pricingPreview ? (config.annualTotals[plan] ?? 0) : annualVariant(plan)?.price ?? config.annualTotals[plan] ?? 0;
    const priceViews = [...root.querySelectorAll('[data-cm-price-view]')];
    function setPriceView(view) {
      priceViews.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.cmPriceView === view)));
      ['monthly', 'standard', 'earlybird'].forEach(plan => {
        const total = plan === 'monthly' ? config.monthlyServicePrice * 10 : annualPrice(plan);
        const amount = view === 'daily' ? total / 180 : view === 'monthly' ? total / 10 : total;
        root.querySelector(`[data-cm-rate="${plan}"]`).textContent = money(amount);
        root.querySelector(`[data-cm-rate-unit="${plan}"]`).textContent = view === 'daily' ? '/ school day' : view === 'monthly' ? (plan === 'monthly' ? '/ mo' : '/ mo equivalent') : '/ school year';
      });
      root.querySelector('[data-cm-price-basis]').textContent = {
        daily: 'Daily service equivalents based on 180 school days. Device and custom color costs are separate. Annual plans are paid up front.',
        monthly: 'Monthly service equivalents based on 10 school months. Annual plans are paid up front. Device and custom color costs are separate.',
        annual: 'Service pricing for the school year, paid up front. Device and custom color costs are separate.'
      }[view];
    }
    priceViews.forEach(button => button.addEventListener('click', () => setPriceView(button.dataset.cmPriceView)));
    setPriceView('annual');
    const lifetimePrice = variant => config.pricingPreview ? config.lifetimeFallback : (variant ? variant.price : config.lifetimeFallback);
    const colorUpgrade = () => state.hardware ? Math.max(0, (selectedVariant()?.price ?? config.hardwareFallback) - config.hardwareFallback) : 0;
    const displayColor = color => ({ Ink: 'Black', Violet: 'Lavender' }[color] || color);
    const colorName = () => displayColor(state.color);
    const deviceSize = root.querySelector('[data-cm-device-size]');
    const extraSize = root.querySelector('[data-cm-extra-size]');
    const sizeNames = { S: 'S (4.75–5.5 in)', M: 'M (5.5–6.25 in)', L: 'L (6.25–7.0+ in)' };
    const bandInputs = [...root.querySelectorAll('[data-cm-band-id]')];
    const selectedBands = () => bandInputs.map(input => ({ variant: config.bands?.variants.find(v => String(v.id) === input.dataset.cmBandId && v.available), size: input.dataset.cmBandSize, quantity: Math.min(20, Math.max(0, Math.floor(Number(input.value) || 0))) })).filter(band => band.variant && band.quantity > 0);
    const bandsCost = () => selectedBands().reduce((total, band) => total + band.variant.price * band.quantity, 0);
    const bandSelect = root.querySelector('[data-cm-band-select]');
    const bandQuantity = root.querySelector('[data-cm-band-quantity]');
    const bandAdd = root.querySelector('[data-cm-band-add]');
    const bandSelections = root.querySelector('[data-cm-band-selections]');
    const bandSwatches = [...(root.querySelectorAll('[data-cm-band-color]') || [])];
    const extraPreview = root.querySelector('[data-cm-extra-preview]');
    const extraColorLabel = root.querySelector('[data-cm-extra-color-label]');
    const bandLabel = id => bandInputs.find(input => input.dataset.cmBandId === String(id))?.dataset.cmBandLabel || 'Band';
    function updateBandPicker() {
      if (!bandAdd || !bandSelect) return;
      const variant = config.bands?.variants.find(band => String(band.id) === bandSelect.value && band.available);
      const current = Number(bandInputs.find(input => input.dataset.cmBandId === bandSelect.value && input.dataset.cmBandSize === extraSize?.value)?.value || 0);
      bandAdd.disabled = !variant || !extraSize?.value || current >= 20;
      bandSwatches.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.cmBandColor === bandSelect.value)));
      const selectedSwatch = bandSwatches.find(button => button.dataset.cmBandColor === bandSelect.value);
      if (extraPreview && selectedSwatch) {
        extraPreview.setAttribute('src', selectedSwatch.dataset.cmBandImage);
        extraPreview.setAttribute('alt', `${bandLabel(bandSelect.value)} extra band`);
      }
      if (extraColorLabel) extraColorLabel.textContent = bandLabel(bandSelect.value);
    }
    if (bandSelect) {
      const firstAvailable = config.bands?.variants.find(band => band.available);
      if (firstAvailable) bandSelect.value = String(firstAvailable.id);
      bandSelect.addEventListener('change', updateBandPicker);
    }
    bandSwatches.forEach(button => button.addEventListener('click', () => {
      if (button.disabled || !bandSelect) return;
      bandSelect.value = button.dataset.cmBandColor;
      updateBandPicker();
    }));
    bandAdd?.addEventListener('click', () => {
      const input = bandInputs.find(input => input.dataset.cmBandId === bandSelect.value && input.dataset.cmBandSize === extraSize?.value);
      const variant = config.bands?.variants.find(band => String(band.id) === bandSelect.value && band.available);
      if (!input || !variant) return;
      const quantity = Math.min(20, Math.max(1, Math.floor(Number(bandQuantity.value) || 1)));
      input.value = String(Math.min(20, Number(input.value || 0) + quantity));
      bandQuantity.value = '1';
      update();
    });
    bandSelections?.addEventListener('click', event => {
      const button = event.target.closest('[data-cm-remove-band]');
      if (!button) return;
      const input = bandInputs.find(input => input.dataset.cmBandId === button.dataset.cmRemoveBand && input.dataset.cmBandSize === button.dataset.cmRemoveSize);
      if (input) input.value = '0';
      update();
      bandAdd?.focus();
    });
    function update() {
      const lifetime = state.plan === 'lifetime';
      const annual = state.plan === 'earlybird' || state.plan === 'standard';
      const requiresPurchase = lifetime || annual || state.hardware || bandsCost() > 0;
      const variant = selectedVariant();
      const lifetimePlan = selectedVariant(config.lifetime);
      root.querySelectorAll('[data-cm-plan]').forEach(card => {
        const selected = card.dataset.cmPlan === state.plan;
        card.setAttribute('aria-current', String(selected));
        const button = card.querySelector('button');
        button.setAttribute('aria-pressed', String(selected));
        const unavailable = card.getAttribute('aria-disabled') === 'true';
        button.disabled = unavailable;
        button.textContent = unavailable ? (card.dataset.cmPlan === 'monthly' ? 'Currently unavailable' : card.dataset.cmPlan === 'standard' ? 'Available after Nov 30' : 'Offer ended') : selected ? '✓ Selected' : 'Choose plan';
        button.classList.toggle('cm-button-outline', !selected);
        if (card.dataset.detailsOpen === undefined) card.dataset.detailsOpen = String(selected);
        const detailsButton = card.querySelector('[data-cm-plan-details]');
        if (detailsButton) {
          const open = card.dataset.detailsOpen === 'true';
          detailsButton.setAttribute('aria-expanded', String(open));
          detailsButton.innerHTML = open ? 'Less detail <span aria-hidden="true">−</span>' : 'View details <span aria-hidden="true">+</span>';
        }

        const cardVariant = card.dataset.cmPlan === 'lifetime' ? lifetimePlan : variant;
        const plan = card.dataset.cmPlan;
        const hardwareCost = state.hardware ? (variant ? variant.price : config.hardwareFallback) : 0;
        const due = plan === 'lifetime' ? lifetimePrice(cardVariant) + colorUpgrade() : hardwareCost + annualPrice(plan);
        card.querySelector('[data-cm-due]').textContent = money(due + bandsCost());
      });
      root.querySelectorAll('[data-cm-hardware]').forEach(button => { button.setAttribute('aria-pressed', String((button.dataset.cmHardware === 'add') === state.hardware)); });
      root.querySelectorAll('[data-cm-color]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.cmColor === state.color)));
      root.querySelector('[data-cm-color-name]').textContent = colorName().toUpperCase();
      root.querySelector('[data-cm-color-note]').textContent = state.color === 'Ink' ? ' · no extra charge' : '';
      const colorPrice = root.querySelector('[data-cm-color-price]');
      if (colorPrice) { colorPrice.hidden = state.color === 'Ink'; colorPrice.textContent = '+$20.00'; }
      const preview = root.querySelector('[data-cm-device-preview]');
      if (preview && variant?.image) {
        if (preview.getAttribute('src') !== variant.image) preview.setAttribute('src', variant.image);
        preview.setAttribute('alt', `Classmate ${colorName()} wearable`);
      }
      root.querySelector('[data-cm-colors]').hidden = !state.hardware;
      root.querySelector('[data-cm-ack-wrap]').hidden = lifetime;
      root.querySelector('[data-cm-subscription-note]').hidden = lifetime;
      const deviceCost = lifetime ? lifetimePrice(lifetimePlan) + colorUpgrade() : state.hardware ? (variant ? variant.price : config.hardwareFallback) : 0;
      root.querySelector('[data-cm-total]').textContent = money(deviceCost + (annual ? annualPrice(state.plan) : 0) + bandsCost());
      const bandTotal = root.querySelector('[data-cm-bands-total]');
      if (bandTotal) bandTotal.textContent = selectedBands().length ? `Extra bands: ${money(bandsCost())} · added to your pre-order` : '';
      const bandSummary = root.querySelector('[data-cm-bands-summary]');
      const bandCount = selectedBands().reduce((total, band) => total + band.quantity, 0);
      if (bandSummary) bandSummary.textContent = bandCount ? `${bandCount} added · ${money(bandsCost())}` : '';
      if (bandSelections) {
        bandSelections.replaceChildren();
        selectedBands().forEach(({ variant: band, size, quantity }) => {
          const item = document.createElement('li');
          const label = document.createElement('span');
          label.textContent = `${bandLabel(band.id)} · ${size} × ${quantity}`;
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.dataset.cmRemoveBand = String(band.id);
          remove.dataset.cmRemoveSize = size;
          remove.setAttribute('aria-label', `Remove ${bandLabel(band.id)} size ${size} extra bands`);
          remove.textContent = '×';
          item.append(label, remove);
          bandSelections.append(item);
        });
      }
      updateBandPicker();
      root.querySelector('[data-cm-ack-text]').textContent = 'I understand the billing and activation terms above.';
      const planNames = { monthly: 'Classmate Monthly', earlybird: 'Earlybird Annual', standard: 'Classmate Annual', lifetime: 'Classmate for Life(time)' };
      root.querySelector('[data-cm-summary-label]').textContent = planNames[state.plan] + (state.hardware ? ` + ${colorName()} device${state.size ? ' · band size ' + state.size : ''}` : ' · existing device') + (bandsCost() ? ` + ${money(bandsCost())} extra bands` : '');
      const billing = root.querySelector('[data-cm-subscription-note]');
      billing.hidden = false;
      billing.textContent = lifetime ? 'One-time payment. Standard black device included; custom colors add $20. No recurring subscription charges.' : annual ? 'Paid up front for the school year. Activate with your first-year code when your device arrives and you register it in the app. No recurring charges during summer break.' : 'Only the device and accessories are charged today. Monthly billing starts after your device arrives and is registered in the app. No recurring charges during summer break.';
      const itemsAvailable = lifetime ? lifetimePlan && (!state.hardware || variant) : annual ? annualVariant(state.plan) && (!state.hardware || variant) : requiresPurchase && (!state.hardware || variant);
      const ready = itemsAvailable && (!state.hardware || !!state.size) && (lifetime || acknowledgment.checked);
      checkout.disabled = config.pricingPreview || !ready;
      checkout.textContent = config.pricingPreview ? 'Preview only · purchasing disabled' : !requiresPurchase ? 'No purchase due today' : !itemsAvailable ? 'Pre-orders opening soon' : 'Add pre-order to cart →';
      error.hidden = true;
    }
    root.querySelectorAll('[data-cm-plan] .cm-button').forEach(button => button.addEventListener('click', () => { if (button.disabled) return; state.plan = button.closest('[data-cm-plan]').dataset.cmPlan; root.querySelectorAll('[data-cm-plan]').forEach(card => card.dataset.detailsOpen = String(card.dataset.cmPlan === state.plan)); acknowledgment.checked = false; update(); }));
    root.querySelectorAll('[data-cm-plan-details]').forEach(button => button.addEventListener('click', () => { const card = button.closest('[data-cm-plan]'); card.dataset.detailsOpen = String(card.dataset.detailsOpen !== 'true'); update(); }));
    const bandsDisclosure = root.querySelector('.cm-extra-bands');
    bandsDisclosure?.addEventListener('toggle', () => { const label = root.querySelector('[data-cm-bands-action]'); if (label) label.textContent = bandsDisclosure.open ? 'Close options' : 'Choose bands'; });
    root.querySelectorAll('[data-cm-hardware]').forEach(button => button.addEventListener('click', () => { state.hardware = !config.allowExistingDevice || button.dataset.cmHardware === 'add'; update(); }));
    root.querySelectorAll('[data-cm-color]').forEach(button => button.addEventListener('click', () => { root.querySelector('[data-cm-device-preview]')?.dispatchEvent(new Event('cm:band-select')); state.color = button.dataset.cmColor; update(); }));
    bandInputs.forEach(input => {
      input.addEventListener('input', update);
      input.addEventListener('change', () => { input.value = String(Math.min(20, Math.max(0, Math.floor(Number(input.value) || 0)))); update(); });
    });
    deviceSize?.addEventListener('change', () => { state.size = deviceSize.value; update(); });
    extraSize?.addEventListener('change', updateBandPicker);
    acknowledgment.addEventListener('change', update);
    checkout.addEventListener('click', async () => {
      const variant = selectedVariant();
      if (checkout.disabled) return;
      checkout.disabled = true; checkout.textContent = 'Adding your pre-order…';
      try {
        const planNames = { monthly: 'Classmate Monthly', earlybird: 'Earlybird Annual', standard: 'Classmate Annual', lifetime: 'Classmate for Life(time)' };
        const properties = { 'Selected plan': planNames[state.plan], 'Expected ship date': 'December 15, 2026' };
        const items = [];
        const annual = state.plan === 'earlybird' || state.plan === 'standard';
        if (annual) {
          const planVariant = annualVariant(state.plan);
          if (!planVariant) throw new Error('This annual plan is currently unavailable.');
          items.push({ id: planVariant.id, quantity: 1, properties: { 'Selected plan': planNames[state.plan], 'Coverage': 'First year · 10 active school months', 'Activation': 'Activate in the app after device arrival', '_Activation code fulfillment': 'Manual code delivery required', 'Device': state.hardware ? `New device · ${colorName()}` : 'Existing device' } });
        }
        if (state.plan === 'lifetime') {
          const lifetimePlan = selectedVariant(config.lifetime);
          if (!lifetimePlan) throw new Error('The lifetime plan is currently unavailable.');
          items.push({ id: lifetimePlan.id, quantity: 1, properties: { 'Selected plan': planNames.lifetime, 'Device': state.hardware ? 'Standard device included; custom color upgrade charged separately' : 'Existing device' } });
        }
        if (state.hardware) {
          if (!variant) throw new Error('The selected device color is currently unavailable.');
          properties['Device color'] = colorName();
          properties['Wristband size'] = sizeNames[state.size];
          if (colorUpgrade()) properties['Custom color upgrade'] = `${money(colorUpgrade())} included in device price`;
          if (state.plan === 'monthly') properties['Subscription terms'] = `${money(config.monthlyServicePrice)} per active school month; billing begins after device arrival and app registration. No monthly subscription charged today.`;
          if (state.plan === 'lifetime') properties['Device benefit'] = 'Standard device included with Lifetime · $80 discount';
          items.push({ id: variant.id, quantity: 1, properties });
        }
        selectedBands().forEach(({ variant: band, size, quantity }) => items.push({ id: band.id, quantity, properties: { 'Accessory': 'Additional band only', 'Wristband size': sizeNames[size] } }));
        const response = await fetch(config.cartAdd, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Could not add your pre-order. Please try again.');
        window.location.assign(config.cart);
      } catch (err) { update(); error.textContent = err.message; error.hidden = false; }
    });
    update();
  }
  document.querySelectorAll('[data-cm-pricing]').forEach(initializePricing);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-cm-pricing]').forEach(initializePricing));
})();
